/**
 * Staff side of the customer portal: customers, their portal logins, projects, milestones, what comes next
 * and feedback. Edits go to Supabase under RLS (directors and admins); logins go through the API
 * (Supabase Auth admin) so each change is audited with who made it.
 */
import { adminBlob, adminFetch } from "./api";
import { PREVIEW } from "./preview";
import { ninetyDaysAgo, type HostKind, type HostStatus, type NoticeSeverity, type StatusHost, type StatusNotice, type UptimeDay } from "./status";
import { supabase } from "./supabase";

export type CustomerRole = "owner" | "member" | "accounts";
export type MilestoneStatus = "not_started" | "in_progress" | "ready_for_review" | "approved";
export type FeedbackStatus = "received" | "in_progress" | "done";

export interface CustomerRow { id: string; name: string; abn: string | null; contact_name: string | null; email: string | null; phone: string | null }
export interface CustomerLogin { id: string; email: string; full_name: string; position: string | null; role: CustomerRole; active: boolean; last_sign_in_at: string | null }
export interface StaffProject {
  id: string; customer_id: string; name: string; summary: string | null; stage: string; staging_url: string | null; live_url: string | null;
  started_on: string | null; target_launch: string | null; archived: boolean;
}
export interface StaffMilestone {
  id: string; project_id: string; position: number; title: string; description: string | null; target_date: string | null;
  status: MilestoneStatus; percent_complete: number; review_notes: string | null; ready_at: string | null; approved_at: string | null;
}
export interface StaffRoadmapItem { id: string; project_id: string; position: number; title: string; description: string | null; timing: string | null; status: "planned" | "in_progress" | "done" }
export interface StaffFeedback {
  id: string; customer_id: string; project_id: string; milestone_id: string | null; page_url: string | null; body: string; status: FeedbackStatus;
  author_name: string; reply: string | null; replied_by_name: string | null; replied_at: string | null; created_at: string;
  feedback_files: { id: string; file_name: string; size_bytes: number }[];
}
export interface StaffReview { id: string; milestone_id: string; decision: "approved" | "changes_requested"; comments: string | null; reviewer_name: string; created_at: string }
export interface CustomerEvent { id: number; action: string; created_at: string; detail: Record<string, unknown>; user_id: string | null; staff_id: string | null }

export type RequestStatus = "received" | "quoted" | "in_progress" | "done";
export type QuoteStatus = "sent" | "accepted" | "declined" | "cancelled";
export interface StaffPlan { id: string; customer_id: string; name: string; description: string | null; monthly_fee: number | null; modules: string[]; starts_on: string | null }
export interface StaffRequestMessage { id: string; request_id: string; author_kind: "customer" | "staff"; author_name: string; body: string; created_at: string }
export interface StaffRequest {
  id: string; customer_id: string; reference: string; kind: "change" | "support"; title: string; details: string; urgency: "low" | "normal" | "urgent";
  page_url: string | null; addon_key: string | null; status: RequestStatus; author_name: string; quote_id: string | null; created_at: string; last_message_at: string | null;
  service_request_files: { id: string; file_name: string; size_bytes: number }[];
  service_request_messages: StaffRequestMessage[];
}
export interface StaffQuoteLine { description: string; quantity: number; unit: string | null; unit_price: number; amount: number }
export interface StaffQuote {
  id: string; customer_id: string; quote_number: string; source: "addon" | "request"; addon_key: string | null; request_id: string | null; title: string; description: string | null;
  lines: StaffQuoteLine[]; placeholder: boolean; gst_rate: number; subtotal: number; gst: number; total: number; status: QuoteStatus; valid_until: string | null;
  invoice_on_accept: boolean; created_at: string; accepted_at: string | null; declined_at: string | null; decline_reason: string | null; cancelled_at: string | null;
}
export interface StaffInvoice { id: string; customer_id: string; reference: string; service_quote_id: string; description: string; total: number; status: "not_synced" | "synced" | "void"; issued_on: string }
export interface StaffServices { plan: StaffPlan | null; requests: StaffRequest[]; quotes: StaffQuote[]; invoices: StaffInvoice[] }
const NO_SERVICES: StaffServices = { plan: null, requests: [], quotes: [], invoices: [] };

export interface CustomerSummary extends CustomerRow { projects: Pick<StaffProject, "id" | "name" | "stage" | "archived">[]; logins: number; openFeedback: number; openRequests: number }
export interface CustomerDetail {
  customer: CustomerRow;
  logins: CustomerLogin[];
  projects: StaffProject[];
  milestones: StaffMilestone[];
  roadmap: StaffRoadmapItem[];
  feedback: StaffFeedback[];
  reviews: StaffReview[];
  events: CustomerEvent[];
  /** Live status (when the customer portal's status section is on). */
  status: StaffStatus;
  /** Plan, requests, quotes and invoice records (when Services or Requests are on). */
  services: StaffServices;
  /** The ownership register, uploaded documents and handover packs (when Ownership or Documents are on). */
  ownership: StaffOwnership;
}

export type OwnershipKind = "domain" | "cloudflare" | "database" | "repository" | "mailbox" | "payments" | "accounting" | "other";
export interface StaffOwnershipEntry {
  id: string; customer_id: string; kind: OwnershipKind; label: string; provider: string | null; account_ref: string | null; admin_name: string | null;
  renews_on: string | null; region: string | null; access_note: string | null; position: number; updated_at: string;
}
export type DocumentCategory = "agreement" | "financial" | "report" | "technical" | "other";
export interface StaffDocument {
  id: string; customer_id: string; title: string; category: DocumentCategory; description: string | null; file_name: string; content_type: string; size_bytes: number;
  uploaded_by_name: string; created_at: string;
}
export interface StaffHandoverPack { id: string; made_by_name: string; created_at: string }
export interface StaffOwnership { entries: StaffOwnershipEntry[]; documents: StaffDocument[]; packs: StaffHandoverPack[] }
const NO_OWNERSHIP: StaffOwnership = { entries: [], documents: [], packs: [] };

export interface StaffStatus {
  hosts: StatusHost[];
  current: HostStatus[];
  days: UptimeDay[];
  notices: StatusNotice[];
  reports: { id: string; month: string; generated_at: string; generated_by: string | null }[];
}
const NO_STATUS: StaffStatus = { hosts: [], current: [], days: [], notices: [], reports: [] };

function rows<T>(res: { data: unknown; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}
const fixtures = () => import("../dev/customers-fixtures");

let orgId: Promise<string> | null = null;
/** The signed-in staff member's organisation (new rows need it). */
function myOrg(): Promise<string> {
  orgId ??= (async () => {
    const { data } = await supabase.auth.getUser();
    const r = rows<{ organisation_id: string } | null>(await supabase.from("profiles").select("organisation_id").eq("id", data.user?.id ?? "").maybeSingle());
    if (!r) throw new Error("Your staff profile could not be loaded.");
    return r.organisation_id;
  })();
  return orgId;
}

const FEEDBACK_COLUMNS = "id, customer_id, project_id, milestone_id, page_url, body, status, author_name, reply, replied_by_name, replied_at, created_at, feedback_files(id, file_name, size_bytes)";

export async function listCustomers(): Promise<{ customers: CustomerSummary[]; openFeedback: (StaffFeedback & { customer: string })[]; openRequests: (StaffRequest & { customer: string })[] }> {
  if (PREVIEW) return (await fixtures()).list();
  const [customers, projects, logins, feedback, requests] = await Promise.all([
    supabase.from("customers").select("id, name, abn, contact_name, email, phone").order("name"),
    supabase.from("projects").select("id, customer_id, name, stage, archived").order("created_at"),
    supabase.from("customer_users").select("id, customer_id").eq("active", true),
    supabase.from("feedback").select(FEEDBACK_COLUMNS).neq("status", "done").order("created_at"),
    __OPS_PORTAL__.customerRequests ? supabase.from("service_requests").select(REQUEST_COLUMNS).neq("status", "done").order("created_at") : Promise.resolve({ data: [], error: null }),
  ]);
  const r = sortRequests(rows<StaffRequest[]>(requests));
  const p = rows<(StaffProject & { customer_id: string })[]>(projects);
  const l = rows<{ customer_id: string }[]>(logins);
  const f = rows<StaffFeedback[]>(feedback);
  const list = rows<CustomerRow[]>(customers);
  return {
    customers: list.map((c) => ({
      ...c,
      projects: p.filter((x) => x.customer_id === c.id),
      logins: l.filter((x) => x.customer_id === c.id).length,
      openFeedback: f.filter((x) => x.customer_id === c.id).length,
      openRequests: r.filter((x) => x.customer_id === c.id).length,
    })),
    openFeedback: f.map((x) => ({ ...x, customer: list.find((c) => c.id === x.customer_id)?.name ?? "" })),
    openRequests: r.map((x) => ({ ...x, customer: list.find((c) => c.id === x.customer_id)?.name ?? "" })),
  };
}

const REQUEST_COLUMNS =
  "id, customer_id, reference, kind, title, details, urgency, page_url, addon_key, status, author_name, quote_id, created_at, last_message_at, service_request_files(id, file_name, size_bytes), service_request_messages(id, request_id, author_kind, author_name, body, created_at)";
const URGENCY_ORDER = { urgent: 0, normal: 1, low: 2 } as const;
/** Urgent first, then oldest first; each thread oldest first. */
function sortRequests(list: StaffRequest[]): StaffRequest[] {
  return list
    .map((r) => ({ ...r, service_request_files: r.service_request_files ?? [], service_request_messages: [...(r.service_request_messages ?? [])].sort((a, b) => a.created_at.localeCompare(b.created_at)) }))
    .sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency] || a.created_at.localeCompare(b.created_at));
}

/** Open customer requests across customers (Today), urgent first. */
export async function openRequests(): Promise<(StaffRequest & { customer: string })[]> {
  if (PREVIEW) return (await fixtures()).list().then((l) => l.openRequests);
  const [requests, customers] = await Promise.all([
    supabase.from("service_requests").select(REQUEST_COLUMNS).neq("status", "done").order("created_at").limit(50),
    supabase.from("customers").select("id, name"),
  ]);
  const names = rows<{ id: string; name: string }[]>(customers);
  return sortRequests(rows<StaffRequest[]>(requests)).map((r) => ({ ...r, customer: names.find((c) => c.id === r.customer_id)?.name ?? "" }));
}

/** Feedback not yet done, and requests not answered yet (received), for the menu badge. */
export async function openFeedbackCount(): Promise<number> {
  if (PREVIEW) return 3;
  const [feedback, requests] = await Promise.all([
    supabase.from("feedback").select("id", { count: "exact", head: true }).neq("status", "done"),
    __OPS_PORTAL__.customerRequests ? supabase.from("service_requests").select("id", { count: "exact", head: true }).eq("status", "received") : Promise.resolve({ count: 0, error: null }),
  ]);
  if (feedback.error) throw feedback.error;
  if (requests.error) throw requests.error;
  return (feedback.count ?? 0) + (requests.count ?? 0);
}

export async function createCustomer(c: Omit<CustomerRow, "id">): Promise<string> {
  if (PREVIEW) return "c1";
  const r = rows<{ id: string }>(await supabase.from("customers").insert({ ...c, organisation_id: await myOrg() }).select("id").single());
  return r.id;
}

export async function getCustomerDetail(id: string): Promise<CustomerDetail | null> {
  if (PREVIEW) return (await fixtures()).detail(id);
  const customer = rows<CustomerRow | null>(await supabase.from("customers").select("id, name, abn, contact_name, email, phone").eq("id", id).maybeSingle());
  if (!customer) return null;
  const status = __OPS_PORTAL__.customerStatus ? customerStatus(id) : Promise.resolve(NO_STATUS);
  const services = __OPS_PORTAL__.customerServices || __OPS_PORTAL__.customerRequests ? customerServices(id) : Promise.resolve(NO_SERVICES);
  const ownership = __OPS_PORTAL__.customerOwnership || __OPS_PORTAL__.customerDocuments ? customerOwnership(id) : Promise.resolve(NO_OWNERSHIP);
  const [logins, projects, milestones, roadmap, feedback, reviews, events] = await Promise.all([
    supabase.from("customer_users").select("id, email, full_name, position, role, active, last_sign_in_at").eq("customer_id", id).order("created_at"),
    supabase.from("projects").select("id, customer_id, name, summary, stage, staging_url, live_url, started_on, target_launch, archived").eq("customer_id", id).order("created_at"),
    supabase.from("milestones").select("id, project_id, position, title, description, target_date, status, percent_complete, review_notes, ready_at, approved_at").eq("customer_id", id).order("position"),
    supabase.from("roadmap_items").select("id, project_id, position, title, description, timing, status").eq("customer_id", id).order("position"),
    supabase.from("feedback").select(FEEDBACK_COLUMNS).eq("customer_id", id).order("created_at", { ascending: false }),
    supabase.from("milestone_reviews").select("id, milestone_id, decision, comments, reviewer_name, created_at").eq("customer_id", id).order("created_at", { ascending: false }),
    supabase.from("customer_events").select("id, action, created_at, detail, user_id, staff_id").eq("customer_id", id).order("created_at", { ascending: false }).limit(30),
  ]);
  return {
    customer,
    logins: rows(logins),
    projects: rows(projects),
    milestones: rows(milestones),
    roadmap: rows(roadmap),
    feedback: rows<StaffFeedback[]>(feedback).map((f) => ({ ...f, feedback_files: f.feedback_files ?? [] })),
    reviews: rows(reviews),
    events: rows(events),
    status: await status,
    services: await services,
    ownership: await ownership,
  };
}

async function customerOwnership(id: string): Promise<StaffOwnership> {
  const [entries, documents, packs] = await Promise.all([
    supabase.from("ownership_entries").select("id, customer_id, kind, label, provider, account_ref, admin_name, renews_on, region, access_note, position, updated_at").eq("customer_id", id).order("position").order("created_at"),
    supabase.from("customer_documents").select("id, customer_id, title, category, description, file_name, content_type, size_bytes, uploaded_by_name, created_at").eq("customer_id", id).order("created_at", { ascending: false }),
    supabase.from("handover_packs").select("id, made_by_name, created_at").eq("customer_id", id).order("created_at", { ascending: false }).limit(10),
  ]);
  return { entries: rows(entries), documents: rows(documents), packs: rows(packs) };
}

/* ------------------------------------------------------------------ ownership and documents (staff) */

export type EntryFields = Omit<StaffOwnershipEntry, "id" | "customer_id" | "updated_at">;
/** Save a register entry under RLS (directors and admins; the database refuses secret-looking values). */
export async function saveEntry(customerId: string, id: string | null, f: EntryFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("ownership_entries").update(f).eq("id", id));
  else rows(await supabase.from("ownership_entries").insert({ ...f, customer_id: customerId, organisation_id: await myOrg() }));
}
export async function deleteEntry(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("ownership_entries").delete().eq("id", id));
}
/** Make the customer's handover pack now and open the PDF. */
export async function openHandoverPack(customerId: string, packId?: string): Promise<void> {
  if (PREVIEW) return;
  const tab = window.open("", "_blank");
  const id = packId ?? (await adminFetch<{ id: string }>(`/customers/${customerId}/handover-packs`, { method: "POST", body: "{}" })).id;
  const url = URL.createObjectURL(await adminBlob(`/customers/${customerId}/handover-packs/${id}/pdf`, {}, { timeoutMs: 60_000 }));
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
export async function uploadDocument(customerId: string, f: { title: string; category: DocumentCategory; description: string; file: File }): Promise<void> {
  if (PREVIEW) return;
  const form = new FormData();
  form.append("title", f.title);
  form.append("category", f.category);
  if (f.description) form.append("description", f.description);
  form.append("file", f.file);
  await adminFetch(`/customers/${customerId}/documents`, { method: "POST", body: form }, { timeoutMs: 120_000 });
}
export async function removeDocument(customerId: string, id: string): Promise<void> {
  if (PREVIEW) return;
  await adminFetch(`/customers/${customerId}/documents/${id}`, { method: "DELETE" });
}
export async function openDocument(id: string, fileName: string): Promise<void> {
  if (PREVIEW) return;
  const url = URL.createObjectURL(await adminBlob(`/customer-documents/${id}`, {}, { timeoutMs: 60_000 }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function customerServices(id: string): Promise<StaffServices> {
  const [plan, requests, quotes, invoices] = await Promise.all([
    supabase.from("customer_plans").select("id, customer_id, name, description, monthly_fee, modules, starts_on").eq("customer_id", id).maybeSingle(),
    supabase.from("service_requests").select(REQUEST_COLUMNS).eq("customer_id", id).order("created_at", { ascending: false }).limit(100),
    supabase
      .from("service_quotes")
      .select("id, customer_id, quote_number, source, addon_key, request_id, title, description, lines, placeholder, gst_rate, subtotal, gst, total, status, valid_until, invoice_on_accept, created_at, accepted_at, declined_at, decline_reason, cancelled_at")
      .eq("customer_id", id)
      .order("created_at", { ascending: false })
      .limit(100),
    supabase.from("service_invoices").select("id, customer_id, reference, service_quote_id, description, total, status, issued_on").eq("customer_id", id).order("created_at", { ascending: false }),
  ]);
  const p = rows<StaffPlan | null>(plan);
  const open = (r: StaffRequest) => r.status !== "done";
  const reqs = rows<StaffRequest[]>(requests);
  return {
    plan: p ? { ...p, monthly_fee: p.monthly_fee === null ? null : Number(p.monthly_fee), modules: p.modules ?? [] } : null,
    requests: [...sortRequests(reqs.filter(open)), ...sortRequests(reqs.filter((r) => !open(r))).reverse()],
    quotes: rows<StaffQuote[]>(quotes).map((q) => ({
      ...q, gst_rate: Number(q.gst_rate), subtotal: Number(q.subtotal), gst: Number(q.gst), total: Number(q.total),
      lines: (q.lines ?? []).map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price), amount: Number(l.amount) })),
    })),
    invoices: rows<StaffInvoice[]>(invoices).map((i) => ({ ...i, total: Number(i.total) })),
  };
}

/* ------------------------------------------------------------------ services and requests (staff) */

export interface PlanFields { name: string; description: string | null; monthly_fee: number | null; modules: string[]; starts_on: string | null }
export async function savePlan(customerId: string, id: string | null, f: PlanFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("customer_plans").update(f).eq("id", id));
  else rows(await supabase.from("customer_plans").insert({ ...f, customer_id: customerId, organisation_id: await myOrg() }));
}
export async function deletePlan(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("customer_plans").delete().eq("id", id));
}

/** Reply to a request and/or set its status (staff_request_update, recorded with the signed-in staff member). */
export async function updateRequest(id: string, status: RequestStatus | null, reply: string | null): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.rpc("staff_request_update", { p_request: id, p_status: status, p_reply: reply }));
}

export interface QuoteFields {
  title: string;
  description: string | null;
  lines: { description: string; quantity: number; unit: string | null; unit_price: number }[];
  validUntil: string | null;
  invoiceOnAccept: boolean;
  note: string | null;
}
/** Turn a request into a quote: it waits under Services for an owner to sign, and the request is marked quoted. */
export async function quoteRequest(requestId: string, f: QuoteFields): Promise<void> {
  if (PREVIEW) return;
  rows(
    await supabase.rpc("staff_request_quote", {
      p_request: requestId,
      p_title: f.title,
      p_description: f.description,
      p_lines: f.lines,
      p_gst_rate: __OPS_PORTAL__.servicesGst ? 0.1 : 0,
      p_valid_until: f.validUntil,
      p_invoice: f.invoiceOnAccept,
      p_note: f.note,
    }),
  );
}
export async function cancelQuote(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.rpc("staff_cancel_quote", { p_quote: id }));
}
export async function openRequestFile(id: string): Promise<void> {
  if (PREVIEW) return;
  const tab = window.open("", "_blank");
  const url = URL.createObjectURL(await adminBlob(`/request-files/${id}`));
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function customerStatus(id: string): Promise<StaffStatus> {
  const [hosts, current, days, notices, reports] = await Promise.all([
    supabase.from("monitored_hosts").select("id, url, label, kind, position").eq("customer_id", id).order("position").order("created_at"),
    supabase.from("host_status").select("host_id, status, checked_at, http_status, response_ms, error, version, commit_sha, deployed_at, version_seen_at").eq("customer_id", id),
    supabase.from("host_uptime_daily").select("host_id, day, checks, up, degraded, down").eq("customer_id", id).gte("day", ninetyDaysAgo()),
    supabase.from("status_notices").select("id, title, body, severity, starts_at, ends_at").eq("customer_id", id).order("starts_at", { ascending: false }).limit(30),
    supabase.from("status_reports").select("id, month, generated_at, generated_by").eq("customer_id", id).order("month", { ascending: false }).limit(24),
  ]);
  return { hosts: rows(hosts), current: rows(current), days: rows(days), notices: rows(notices), reports: rows(reports) };
}

/* ------------------------------------------------------------------ live status (staff) */

export interface HostFields { url: string; label: string; kind: HostKind; position: number }
export async function saveHost(customerId: string, id: string | null, f: HostFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("monitored_hosts").update(f).eq("id", id));
  else rows(await supabase.from("monitored_hosts").insert({ ...f, customer_id: customerId, organisation_id: await myOrg() }));
}
export async function deleteHost(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("monitored_hosts").delete().eq("id", id));
}

export interface NoticeFields { title: string; body: string; severity: NoticeSeverity; starts_at: string; ends_at: string | null }
export async function saveNotice(customerId: string, id: string | null, f: NoticeFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("status_notices").update(f).eq("id", id));
  else rows(await supabase.from("status_notices").insert({ ...f, customer_id: customerId, organisation_id: await myOrg() }));
}
export async function deleteNotice(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("status_notices").delete().eq("id", id));
}

/** Check this customer's hosts now (the API; otherwise every 15 minutes). */
export async function checkHostsNow(customerId: string): Promise<{ checked: number }> {
  if (PREVIEW) return { checked: 2 };
  return adminFetch(`/customers/${customerId}/status/check`, { method: "POST", body: "{}" }, { timeoutMs: 60_000 });
}
/** Make (or remake) a monthly report; month "YYYY-MM", default last month. */
export async function generateReport(customerId: string, month?: string): Promise<void> {
  if (PREVIEW) return;
  await adminFetch(`/customers/${customerId}/reports`, { method: "POST", body: JSON.stringify(month ? { month } : {}) }, { timeoutMs: 60_000 });
}
export async function openReportPdf(customerId: string, reportId: string): Promise<void> {
  if (PREVIEW) return;
  const tab = window.open("", "_blank");
  const url = URL.createObjectURL(await adminBlob(`/customers/${customerId}/reports/${reportId}/pdf`, {}, { timeoutMs: 60_000 }));
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function updateCustomer(id: string, patch: Partial<Omit<CustomerRow, "id">>): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("customers").update(patch).eq("id", id));
}

export type ProjectFields = Omit<StaffProject, "id" | "customer_id">;
export async function saveProject(customerId: string, id: string | null, f: ProjectFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("projects").update(f).eq("id", id));
  else rows(await supabase.from("projects").insert({ ...f, customer_id: customerId, organisation_id: await myOrg() }));
}

export type MilestoneFields = Pick<StaffMilestone, "title" | "description" | "target_date" | "status" | "percent_complete" | "review_notes" | "position">;
export async function saveMilestone(customerId: string, projectId: string, id: string | null, f: MilestoneFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("milestones").update(f).eq("id", id));
  else rows(await supabase.from("milestones").insert({ ...f, customer_id: customerId, project_id: projectId, organisation_id: await myOrg() }));
}
export async function deleteMilestone(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("milestones").delete().eq("id", id));
}

export type RoadmapFields = Pick<StaffRoadmapItem, "title" | "description" | "timing" | "status" | "position">;
export async function saveRoadmapItem(customerId: string, projectId: string, id: string | null, f: RoadmapFields): Promise<void> {
  if (PREVIEW) return;
  if (id) rows(await supabase.from("roadmap_items").update(f).eq("id", id));
  else rows(await supabase.from("roadmap_items").insert({ ...f, customer_id: customerId, project_id: projectId, organisation_id: await myOrg() }));
}
export async function deleteRoadmapItem(id: string): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("roadmap_items").delete().eq("id", id));
}

export async function answerFeedback(id: string, patch: { status?: FeedbackStatus; reply?: string | null }): Promise<void> {
  if (PREVIEW) return;
  rows(await supabase.from("feedback").update(patch).eq("id", id));
}

export async function inviteLogin(customerId: string, body: { email: string; fullName: string; position?: string; role: CustomerRole; sendEmail: boolean }) {
  if (PREVIEW) return { created: true, emailed: body.sendEmail };
  return adminFetch<{ created: boolean; emailed: boolean }>(`/customers/${customerId}/logins`, { method: "POST", body: JSON.stringify(body) });
}
export async function changeLogin(customerId: string, loginId: string, patch: { role?: CustomerRole; active?: boolean; fullName?: string; position?: string | null }) {
  if (PREVIEW) return;
  await adminFetch(`/customers/${customerId}/logins/${loginId}`, { method: "PATCH", body: JSON.stringify(patch) });
}
export async function loginLink(customerId: string, loginId: string): Promise<string> {
  if (PREVIEW) return "https://example.com/preview-sign-in-link";
  return (await adminFetch<{ url: string }>(`/customers/${customerId}/logins/${loginId}/link`, { method: "POST", body: "{}" })).url;
}
export async function openCustomerFile(id: string): Promise<void> {
  if (PREVIEW) return;
  const tab = window.open("", "_blank");
  const url = URL.createObjectURL(await adminBlob(`/customer-files/${id}`));
  if (tab) tab.location.href = url;
  else window.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
